/* eslint-disable react-hooks/exhaustive-deps */
/*
  AdminContact — read-only inbox for contact-form submissions.
*/
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Inbox, Archive, CheckCircle2, Mail, Eye } from "lucide-react";

export function AdminContact() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("unread");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const load = async () => {
    const r = await api.get(`/admin/contact?status=${filter}`);
    setItems(r.data?.items || []);
    setCounts(r.data?.counts || {});
  };
  useEffect(() => { load(); }, [filter]);

  const markRead = async (id) => {
    setBusy(true);
    try { await api.post(`/admin/contact/${id}/mark-read`); await load(); }
    finally { setBusy(false); }
  };
  const archive = async (id) => {
    if (!window.confirm("Archive this message?")) return;
    setBusy(true);
    try { await api.post(`/admin/contact/${id}/archive`); await load(); setOpen(null); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-contact">
      <section className="ss-card p-6">
        <div className="flex items-center gap-2">
          <Inbox size={14} className="text-mauve" />
          <p className="text-[11px] tracking-[0.35em] uppercase text-mauve">Contact form inbox</p>
        </div>
        <h2 className="font-heading text-2xl text-cocoa mt-1">Read-only feedback &amp; messages.</h2>
        <p className="text-cocoa/65 text-sm mt-1">
          Submissions from the public <code>/contact</code> form. Mark as read once
          processed; archive when handled. Replies are sent from your own email — this
          surface is intentionally read-only.
        </p>
        <div className="mt-4 flex gap-1 flex-wrap">
          {[
            ["unread",   `Unread (${counts.unread ?? 0})`],
            ["read",     `Read (${counts.read ?? 0})`],
            ["archived", `Archived (${counts.archived ?? 0})`],
            ["all",      "All"],
          ].map(([k, label]) => (
            <button key={k} data-testid={`contact-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-3 h-9 text-xs rounded-full ${filter === k ? "bg-cocoa text-ivory" : "bg-champagne/40 text-cocoa/70"}`}>
              {label}
            </button>
          ))}
        </div>
      </section>

      <section className="mt-4 space-y-2">
        {items.length === 0 && (
          <div data-testid="contact-empty" className="ss-card p-6 text-cocoa/60 text-sm">
            No messages in this view.
          </div>
        )}
        {items.map((m) => (
          <div key={m.id} data-testid={`contact-msg-${m.id}`}
            className={`ss-card p-5 ${m.status === "unread" ? "border-mauve" : ""}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Mail size={12} className="text-mauve shrink-0" />
                  <span className="font-heading text-lg text-cocoa">{m.subject}</span>
                  {m.status === "unread" && (
                    <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-mauve text-ivory">new</span>
                  )}
                </div>
                <p className="mt-1 text-[10px] tracking-widest uppercase text-cocoa/55">
                  From <strong>{m.name}</strong> · <a href={`mailto:${m.email}`} className="text-mauve">{m.email}</a> · {new Date(m.created_at).toLocaleString()} · {m.message.length} chars{m.user_agent ? ` · ${/iPhone|iPad/.test(m.user_agent) ? "iOS" : /Android/.test(m.user_agent) ? "Android" : /Mac/.test(m.user_agent) ? "Mac" : /Windows/.test(m.user_agent) ? "Windows" : "web"}` : ""}
                </p>
                {m.message.length < 40 && !/[.!?…]\s*$/.test(m.message.trim()) && (
                  <p data-testid={`contact-msg-${m.id}-truncated`} className="mt-2 text-xs text-cocoa/75 bg-gold/15 border border-gold/40 rounded-md px-2.5 py-1.5">
                    This message looks cut off — it's very short and doesn't end in punctuation. This is everything the sender's device submitted; consider replying to ask them to resend.
                  </p>
                )}
                <div className={`mt-3 text-cocoa/80 text-sm whitespace-pre-wrap ${open === m.id ? "" : "line-clamp-3"}`}>
                  {m.message}
                </div>
                {m.message.length > 200 && (
                  <button data-testid={`contact-msg-${m.id}-toggle`}
                    onClick={() => setOpen(open === m.id ? null : m.id)}
                    className="mt-1 text-xs text-mauve hover:underline">
                    {open === m.id ? "Collapse" : "Read full message"}
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-1 items-end">
                {m.status === "unread" && (
                  <button data-testid={`contact-msg-${m.id}-read`} onClick={() => markRead(m.id)} disabled={busy}
                    className="ss-pill ss-pill-ghost h-8 text-[11px]">
                    <CheckCircle2 size={12} /> Mark read
                  </button>
                )}
                {m.status !== "archived" && (
                  <button data-testid={`contact-msg-${m.id}-archive`} onClick={() => archive(m.id)} disabled={busy}
                    className="ss-pill ss-pill-ghost h-8 text-[11px]">
                    <Archive size={12} /> Archive
                  </button>
                )}
                <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}
                  data-testid={`contact-msg-${m.id}-reply`}
                  className="ss-pill ss-pill-primary h-8 text-[11px]">
                  <Mail size={11} /> Reply by email
                </a>
              </div>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
